import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';

// Poster and flyer codes, the practices that got them and their patient chat
// counts, from Rudi's `/cortex/recruitment` (Rudi ADR 0070). Rudi copies the
// counts from the patient chat every hour.

export interface ActivityTotals {
  scans: number;
  returnVisits: number;
  chatsStarted: number;
  patientMessages: number;
  emailsSent: number;
}

export interface SiteTeam {
  tenantId: string;
  tenantName: string;
  teamId: string;
  teamName: string;
  practiceId: string;
}

export interface RecruitmentSite {
  id: string;
  name: string;
  doctors: string[];
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  city: string | null;
  network: string | null;
  isCenter: boolean;
  teams: SiteTeam[];
  totals: ActivityTotals;
}

export type CodeKind = 'poster' | 'flyer' | 'source';
export type CodePurpose = 'practice' | 'campaign' | 'test';

export interface RecruitmentCode {
  id: string;
  kind: CodeKind;
  firstNumber: number | null;
  lastNumber: number | null;
  source: string | null;
  purpose: CodePurpose;
  siteId: string | null;
  network: string | null;
  note: string | null;
  label: string;
  url: string;
  totals: ActivityTotals;
}

export interface UnclaimedSource {
  source: string;
  totals: ActivityTotals;
  lastDay: string;
}

export interface RecruitmentOverview {
  sites: RecruitmentSite[];
  codes: RecruitmentCode[];
  unclaimed: UnclaimedSource[];
  startDate: string | null;
  endDate: string | null;
  lastSyncedAt: string | null;
  syncConfigured: boolean;
}

export interface RecruitmentImportResult {
  sitesCreated: number;
  sitesUpdated: number;
  codesCreated: number;
  codesUpdated: number;
}

export interface NetworkAssignmentResult {
  practicesCreated: number;
  practicesLinked: number;
  linksCreated: number;
}

export interface SyncResult {
  sourceDays: number;
  linkDays: number;
}

export interface CodeUpdate {
  siteId?: string | null;
  purpose?: CodePurpose;
  network?: string | null;
  note?: string | null;
}

const BASE = '/cortex/recruitment';
export const RECRUITMENT_KEY = ['recruitment'] as const;

function status(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** Rudi's reason for a refused import or edit. */
export function recruitmentError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown; message?: unknown } } })
    ?.response?.data;
  const text = detail?.detail ?? detail?.message;
  // A route this Rudi release lacks gets FastAPI's generic answer.
  if (text === 'Not Found' || text === 'Method Not Allowed') {
    return 'This Rudi release cannot do this yet.';
  }
  if (typeof text === 'string' && text) return text;
  if (Array.isArray(text)) {
    const messages = text
      .map(item => (item as { msg?: unknown })?.msg)
      .filter((msg): msg is string => typeof msg === 'string');
    if (messages.length) return messages.join(' ');
  }
  return error instanceof Error ? error.message : 'Rudi refused the request';
}

/** The overview, or null from a Rudi release without QR codes. */
export function useRecruitmentOverview(range: { startDate?: string; endDate?: string }) {
  const api = useApiClient();
  return useQuery({
    queryKey: [...RECRUITMENT_KEY, range.startDate ?? null, range.endDate ?? null],
    queryFn: async () => {
      try {
        return (
          await api.get<RecruitmentOverview>(BASE, {
            params: {
              ...(range.startDate ? { start_date: range.startDate } : {}),
              ...(range.endDate ? { end_date: range.endDate } : {}),
            },
          })
        ).data;
      } catch (error) {
        const code = status(error);
        if (code === 404 || code === 405 || code === 501) return null;
        throw error;
      }
    },
  });
}

function useRecruitmentMutation<Input, Output>(send: (input: Input) => Promise<Output>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: () => client.invalidateQueries({ queryKey: RECRUITMENT_KEY }),
  });
}

export function useImportRecruitment() {
  const api = useApiClient();
  return useRecruitmentMutation(
    async (payload: unknown) =>
      (await api.post<RecruitmentImportResult>(`${BASE}/import`, payload)).data
  );
}

export function useAssignNetwork() {
  const api = useApiClient();
  return useRecruitmentMutation(
    async (input: { network: string; tenantId: string; teamId: string }) =>
      (
        await api.post<NetworkAssignmentResult>(
          `${BASE}/networks/${encodeURIComponent(input.network)}/assign`,
          { tenantId: input.tenantId, teamId: input.teamId }
        )
      ).data
  );
}

export function useUpdateCode() {
  const api = useApiClient();
  return useRecruitmentMutation(
    async (input: { codeId: string; update: CodeUpdate }) =>
      (await api.patch<RecruitmentCode>(`${BASE}/codes/${input.codeId}`, input.update)).data
  );
}

export function useSyncRecruitment() {
  const api = useApiClient();
  return useRecruitmentMutation(
    async (range: { startDate?: string; endDate?: string }) =>
      (await api.post<SyncResult>(`${BASE}/sync`, range)).data
  );
}

/** Totals of several rows added up. */
export function sumTotals(rows: ActivityTotals[]): ActivityTotals {
  return rows.reduce<ActivityTotals>(
    (sum, row) => ({
      scans: sum.scans + row.scans,
      returnVisits: sum.returnVisits + row.returnVisits,
      chatsStarted: sum.chatsStarted + row.chatsStarted,
      patientMessages: sum.patientMessages + row.patientMessages,
      emailsSent: sum.emailsSent + row.emailsSent,
    }),
    { scans: 0, returnVisits: 0, chatsStarted: 0, patientMessages: 0, emailsSent: 0 }
  );
}

export interface NetworkSummary {
  /** The network key, or null for practices outside any network. */
  network: string | null;
  sites: RecruitmentSite[];
  codes: RecruitmentCode[];
  teams: SiteTeam[];
  totals: ActivityTotals;
}

/** Group practice codes and sites by network; networks first, by chats. */
export function networkSummaries(overview: RecruitmentOverview): NetworkSummary[] {
  const byNetwork = new Map<string | null, NetworkSummary>();
  const entry = (network: string | null) => {
    let found = byNetwork.get(network);
    if (!found) {
      found = { network, sites: [], codes: [], teams: [], totals: sumTotals([]) };
      byNetwork.set(network, found);
    }
    return found;
  };
  const sites = new Map(overview.sites.map(site => [site.id, site]));
  for (const code of overview.codes) {
    if (code.purpose !== 'practice') continue;
    const site = code.siteId ? sites.get(code.siteId) : undefined;
    entry(code.network ?? site?.network ?? null).codes.push(code);
  }
  for (const site of overview.sites) {
    const networks = new Set(
      overview.codes
        .filter(code => code.siteId === site.id && code.purpose === 'practice')
        .map(code => code.network ?? site.network ?? null)
    );
    if (networks.size === 0) networks.add(site.network);
    for (const network of networks) {
      const summary = entry(network);
      summary.sites.push(site);
      for (const team of site.teams) {
        if (!summary.teams.some(known => known.teamId === team.teamId)) summary.teams.push(team);
      }
    }
  }
  for (const summary of byNetwork.values()) {
    summary.totals = sumTotals(summary.codes.map(code => code.totals));
  }
  return [...byNetwork.values()].sort(
    (a, b) =>
      Number(a.network === null) - Number(b.network === null) ||
      b.totals.chatsStarted - a.totals.chatsStarted ||
      (a.network ?? '').localeCompare(b.network ?? '')
  );
}

// ── Batches ──────────────────────────────────────────────────────────

export interface NumberRange {
  first: number;
  last: number;
}

export interface BatchSite {
  name: string;
  doctors: string[];
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  city: string | null;
  isCenter: boolean;
}

/** An existing site (`siteId`) or a new one (`new`), and the numbers it got. */
export interface BatchPractice {
  siteId?: string;
  new?: BatchSite;
  posters: NumberRange[];
  flyers: NumberRange[];
}

export interface BatchCode {
  kind: CodeKind;
  first?: number;
  last?: number;
  source?: string;
  purpose: 'campaign' | 'test';
  note: string | null;
}

export interface RecruitmentBatch {
  network: string | null;
  tenantId: string | null;
  teamId: string | null;
  note: string | null;
  practices: BatchPractice[];
  codes: BatchCode[];
}

export interface RecruitmentBatchResult {
  sitesCreated: number;
  codesCreated: number;
  practicesCreated: number;
  linksCreated: number;
}

/** Add one mailing: its practices of one network, and campaign or test codes. */
export function useAddBatch() {
  const api = useApiClient();
  return useRecruitmentMutation(
    async (batch: RecruitmentBatch) =>
      (await api.post<RecruitmentBatchResult>(`${BASE}/batches`, batch)).data
  );
}

export function useDeleteCode() {
  const api = useApiClient();
  return useRecruitmentMutation(async (codeId: string) => {
    await api.delete(`${BASE}/codes/${codeId}`);
  });
}

/** `41-44, 47` as ranges, or the first piece that is not a number or range. */
export function parseNumbers(text: string): { ranges: NumberRange[]; error: string | null } {
  const ranges: NumberRange[] = [];
  for (const piece of text.split(/[,;\s]+/).filter(Boolean)) {
    const match = /^(\d+)(?:[-–](\d+))?$/.exec(piece);
    if (!match) return { ranges, error: `"${piece}" is not a number or range like 41-44` };
    const first = Number(match[1]);
    const last = match[2] === undefined ? first : Number(match[2]);
    if (last < first) return { ranges, error: `${piece} runs backwards` };
    ranges.push({ first, last });
  }
  return { ranges, error: null };
}

/** `Neurologie UKM` → `neurologie-ukm`, for a new network's key. */
export function networkKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100);
}

const PREFIX: Record<'poster' | 'flyer', string> = { poster: 'location', flyer: 'flyer' };
const KIND_LABEL: Record<'poster' | 'flyer', string> = { poster: 'Poster', flyer: 'Flyer' };

function rangeLabel(kind: 'poster' | 'flyer', range: NumberRange): string {
  return range.first === range.last
    ? `${KIND_LABEL[kind]} ${range.first}`
    : `${KIND_LABEL[kind]} ${range.first}–${range.last}`;
}

interface Entry {
  kind: CodeKind;
  range?: NumberRange;
  source?: string;
}

function entries(batch: RecruitmentBatch): Entry[] {
  return [
    ...batch.practices.flatMap(practice => [
      ...practice.posters.map(range => ({ kind: 'poster' as const, range })),
      ...practice.flyers.map(range => ({ kind: 'flyer' as const, range })),
    ]),
    ...batch.codes.map(code =>
      code.kind === 'source'
        ? { kind: code.kind, source: code.source }
        : { kind: code.kind, range: { first: code.first ?? 0, last: code.last ?? 0 } }
    ),
  ];
}

/** Every number or source of the batch that a code already has, or that appears twice. */
export function batchClashes(overview: RecruitmentOverview, batch: RecruitmentBatch): string[] {
  const sites = new Map(overview.sites.map(site => [site.id, site.name]));
  const found: string[] = [];
  const seen: Entry[] = [];
  for (const entry of entries(batch)) {
    if (entry.kind === 'source') {
      if (!entry.source) continue;
      if (overview.codes.some(code => code.source === entry.source)) {
        found.push(`${entry.source} already has a code`);
      } else if (seen.some(other => other.source === entry.source)) {
        found.push(`${entry.source} is in the form twice`);
      }
    } else if (entry.range) {
      const { first, last } = entry.range;
      const label = rangeLabel(entry.kind, entry.range);
      for (const code of overview.codes) {
        if (code.kind !== entry.kind || code.firstNumber === null || code.lastNumber === null)
          continue;
        if (code.firstNumber <= last && first <= code.lastNumber) {
          const owner = code.siteId ? sites.get(code.siteId) : null;
          found.push(`${label}: ${code.label} is taken${owner ? ` by ${owner}` : ''}`);
        }
      }
      if (
        seen.some(
          other =>
            other.kind === entry.kind &&
            other.range &&
            other.range.first <= last &&
            first <= other.range.last
        )
      ) {
        found.push(`${label} is in the form twice`);
      }
    }
    seen.push(entry);
  }
  return found;
}

/** Sources without a code that the batch's numbers will claim. */
export function claimedSources(
  overview: RecruitmentOverview,
  batch: RecruitmentBatch
): UnclaimedSource[] {
  const list = entries(batch);
  return overview.unclaimed.filter(unclaimed =>
    list.some(entry => {
      if (entry.kind === 'source') return entry.source === unclaimed.source;
      const match = new RegExp(`^${PREFIX[entry.kind]}_0*(\\d+)$`).exec(unclaimed.source);
      if (!match || !entry.range) return false;
      const number = Number(match[1]);
      return entry.range.first <= number && number <= entry.range.last;
    })
  );
}
